import React, { useState, useCallback, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import moment from "moment";
import Sidebar from "../../../components/Sidebar";
import Breadcrumbs from "../../../components/Breadcrumbs";
import useJobAssignment from "./useJobAssignment";
import ServiceRecordsTable from "./ServiceRecordsTable";
import ServiceRecordModal from "./ServiceRecordModal";
import ArModal from "./ArModel";
import {
  getData,
  getDataWithComp,
  getAlert,
  insertData,
  updateData,
  deleteData,
} from "../../../../utils/SamuiUtils";
import Navbar from "../../../components/Modal/Navbar";
      
import { formatThaiDateToDate, formatThaiDateUi, getCreateDateTime,setCreateDateTime,formatThaiDateUiToDate } from "../../../../utils/DateUtils";

// Constants
const FIELD_OPTIONS = {
  status: [
    { value: "1", label: "รอเข้าพบ" },
    { value: "2", label: "Checkin" },
    { value: "3", label: "เลื่อนนัด" },
    { value: "4", label: "ปิด Job" },
    { value: "13", label: "ยกเลิก" },
  ],
  behavior: [
    { value: "5", label: "ดีมาก" },
    { value: "4", label: "ดี" },
    { value: "3", label: "พอใช้" },
    { value: "2", label: "ควรปรับปรุง" },
    { value: "1", label: "ไม่ดี" },
  ],
  productOK: [
    { value: "1", label: "ถูกต้อง" },
    { value: "0", label: "ไม่ถูกต้อง" },
  ],
  cleanliness: [
    { value: "5", label: "ดีมาก" },
    { value: "4", label: "ดี" },
    { value: "3", label: "พอใช้" },
    { value: "2", label: "ควรปรับปรุง" },
    { value: "1", label: "ไม่ดี" },
  ],
  duration: [
    { value: "8", label: "30 นาที" },
    { value: "7", label: "1 ชั่วโมง 30 นาที" },
    { value: "6", label: "2 ชั่วโมง" },
    { value: "5", label: "2 ชั่วโมง 30 นาที" },
    { value: "4", label: "3 ชั่วโมง" },
    { value: "3", label: "3 ชั่วโมง 30 นาที" },
    { value: "2", label: "4 ชั่วโมง" },
    { value: "1", label: "มากกว่า 4 ชั่วโมง" },
  ],
};

const FIELD_COLS_TEMPLATE = (contactFieldType = "text", contactOptions = []) => [
  {
    fields: [
      {
        name: "documentDate",
        label: "วันที่เอกสาร",
        type: "text",
        required: true,
        readonly: true,
      },
      {
        name: "invoiceRef",
        label: "อ้างถึง Invoice",
        type: "Search",
        readonly: false,
        placeholder: "กรุณาเลือกลูกค้าเพื่อแสดง Invoice",
        table: "API_0701_SO_H",
      },
    ],
  },
  {
    fields: [
      {
        name: "customer",
        label: "ลูกค้า",
        type: "Search",
        table: "Tb_Set_AR",
        required: true,
        readonly: false,
        placeholder: "กรุณาเลือกลูกค้า",
      },
      {
        name: "address",
        label: "ที่อยู่",
        type: "textarea",
        readonly: false,
        placeholder: "กรุณาเลือกลูกค้าเพื่อแสดงที่อยู่",
      },
      {
        name: "contact",
        label: "ผู้ติดต่อ/เบอร์ติดต่อ",
        type: contactFieldType,
        options: contactOptions,
        readonly: false,
        placeholder: "กรุณาเลือกลูกค้าเพื่อแสดงผู้ติดต่อ",
      },
    ],
  },

  {
    fields: [
      {
        name: "Created_Date",
        label: "วันที่สร้างเอกสาร",
        type: "text",
        readonly: true,
      },
      {
        name: "creator",
        label: "ผู้สร้างเอกสาร",
        type: "text",
        readonly: true,
      },
      {
        name: "Update_Date",
        label: "วันที่แก้ไขเอกสารล่าสุด",
        type: "text",
        readonly: true,
      },
      {
        name: "Update_By_Name",
        label: "ผู้แก้ไขเอกสาร",
        type: "text",
        readonly: true,
      },



    ],
  },
];


const getStorageItem = (key) => window.localStorage.getItem(key) || "";

const getInitialFormData = () => ({
  jobNo: "",
  documentDate:formatThaiDateUi(new Date()),
  customer: "",
  creator: getStorageItem("name"),
  serviceType: "",
  address: "",
  invoiceRef: "",
  contact: "",
  dueDate: "",
  remarks: "",
  AR_Id: null,
  AR_Contact1: "",
  AR_Contact2: "",
  Created_Date: getCreateDateTime(),
  
});

const getLabel = (name, value) => {
  const option = FIELD_OPTIONS[name]?.find(
    (opt) => opt.value === value?.toString()
  );
  return option?.label || "";
};

const getLabelFromOptionList = (options, id) => {
  const option = options.find((opt) => opt.value.id === id);
  return option?.label || "";
};


const formatAddress = (ar) => {
  const parts = [ar.AR_Add1, ar.AR_Add2, ar.AR_Add3, ar.AR_Province, ar.AR_Zipcode];
  return parts.filter(Boolean).join(" ");
};

// Custom hooks
const useEmployeeOptions = () => {
  const [employeeOptions, setEmployeeOptions] = useState([]);

  useEffect(() => {
    const fetchEmployees = async () => {
      try {
        const result = await getDataWithComp("tb_set_employee", "Order By Emp_Code ASC");
        const options = result.map((emp) => ({
          value: { id: emp.Emp_Id, name: emp.Emp_Name },
          label: `${emp.Emp_Code} - ${emp.Emp_Prefix} ${emp.Emp_Name} (${emp.Emp_Position_Detail})`,
        }));
        setEmployeeOptions(options);
      } catch (error) {
        console.error("Failed to fetch employees:", error);
      }
    };

    fetchEmployees();
  }, []);

  return employeeOptions;
};

const useServiceTypeOptions = () => {
  const [serviceTypeOptions, setServiceTypeOptions] = useState([]);

  useEffect(() => {
    const fetchServiceTypes = async () => {
      try {
        const result = await getData("Tb_Set_Service", "srv_use = 'Y'");
        const options = result.map((srv) => ({
          value: { id: srv.srv_id, name: srv.srv_name },
          label: srv.srv_name,
        }));
        setServiceTypeOptions(options);
      } catch (error) {
        console.error("Failed to fetch service types:", error);
      }
    };

    fetchServiceTypes();
  }, []);

  return serviceTypeOptions;
};

// Main component
export default function JobAssignmentSystem() {
  const today = moment().format("YYYY-MM-DD");
  const startOfMonth = moment().startOf("month").format("YYYY-MM-DD");
  const { current, generateJobNo } = useJobAssignment({
    startDate: startOfMonth,
    endDate: today
  });
  const navigate = useNavigate();
  const location = useLocation();
  const isEditMode = !!location.state;

  // State
  const [formData, setFormData] = useState(getInitialFormData());
  const [serviceRecords, setServiceRecords] = useState([]);
  const [editingRecord, setEditingRecord] = useState(null);
  const [errors, setErrors] = useState({});
  const [activeModalType, setActiveModalType] = useState(null);
  const [fieldRows, setFieldRows] = useState([]);

  // Custom hooks
  const employeeOptions = useEmployeeOptions();
  const serviceTypeOptions = useServiceTypeOptions();

  // Initialize field rows
const initFieldRows = useCallback(() => {
  const contactOptions = [];
  let contactFieldType = "text";

  if (formData.AR_Contact1 && formData.AR_Contact2) {
    contactFieldType = "select";
    contactOptions.push(
      { value: formData.AR_Contact1, label: formData.AR_Contact1 },
      { value: formData.AR_Contact2, label: formData.AR_Contact2 }
    );
  } else if (formData.AR_Contact1 || formData.AR_Contact2) {
    formData.contact = formData.AR_Contact1 || formData.AR_Contact2;
  }

  const rows = FIELD_COLS_TEMPLATE(contactFieldType, contactOptions).map((group) => group.fields);
  const maxRows = Math.max(...rows.map((col) => col.length));
  const structuredRows = Array.from({ length: maxRows }, (_, rowIndex) =>
    rows.map((col) => col[rowIndex] || null)
  );
  setFieldRows(structuredRows);
}, []); 




  // Initialize form data
  useEffect(() => {
    if (!isEditMode) {
      const newJobNo = generateJobNo(current);
      setFormData(prev => ({ ...prev, jobNo: newJobNo }));
    }
  }, [isEditMode, generateJobNo, current]);


  // Load service records
  const loadServiceRecords = useCallback(async (jobId) => {
    try {
      const result = await getData("JOB_D", `job_id = ${jobId}`);
      const records = result.map((record, index) => ({
        id: index + 1,
        itemNo: record.job_time || index + 1,
        serviceDate: record.srv_date || "",
        nextAppointment: record.app_date || "",
        inquiryDate: record.inq_date || "",
        desc: record.job_desc || "",
        detail: record.job_detail || "",
        improvement: record.improvement || "",
        serviceType: {
          value: { id: record.srv_id, name: record.srv_name },
          label: getLabelFromOptionList(serviceTypeOptions, record.srv_id),
        },
        status: {
          value: record.job_status?.toString(),
          label: getLabel("status", record.job_status),
        },
        behavior: {
          value: record.manners?.toString(),
          label: getLabel("behavior", record.manners),
        },
        productOK: {
          value: record.product_correct?.toString(),
          label: getLabel("productOK", record.product_correct),
        },
        cleanliness: {
          value: record.clean?.toString(),
          label: getLabel("cleanliness", record.clean),
        },
        duration: {
          value: record.working_period?.toString(),
          label: getLabel("duration", record.working_period),
        },
        employee: record.emp_id ? {
          id: record.emp_id,
          name: employeeOptions.find((opt) => opt.value.id === record.emp_id)?.value.name || "",
        } : "",
      }));

      setServiceRecords(records);
    } catch (error) {
      console.error("loadServiceRecords error:", error);
      getAlert("FAILED", "โหลดรายละเอียดบริการไม่สำเร็จ");
    }
  }, [serviceTypeOptions, employeeOptions]);

  // Initialize edit mode data
  const initEditModeData = useCallback(() => {
    if (!location.state) return;

    const data = location.state;
    setFormData({
      ...getInitialFormData(),
      jobNo: data.job_no,
      documentDate: formatThaiDateUi(data.job_date),
      customer: data.AR_Name,
      address: formatAddress(data),
      contact: data.contact_name || "",
      invoiceRef: data.ref_invoice || "",
      creator: data.Created_By_Name || "",
      AR_Id: data.ar_id,
      Update_By_Name: getStorageItem("name"),
      Created_Date: setCreateDateTime(data.Created_Date) || "",
      Update_Date: setCreateDateTime(data.Update_Date) || getCreateDateTime(),
    });

    if (data.job_id) {
      loadServiceRecords(data.job_id);
    }
  }, [location.state, loadServiceRecords]);

  // Effects
  useEffect(() => {
    initFieldRows();
  }, [initFieldRows]);

  useEffect(() => {
    initEditModeData();
  }, [initEditModeData]);

  // Event handlers
  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: "" }));
    }
  }, [errors]);

  const handleDeleteRecord = useCallback(async (id) => {
    // console.log("handleDeleteRecord called with id:", id);
    if (!id) return;

    getAlert("C_DIALOG", "ยืนยันการลบข้อมูล", (confirmed) => {
      if (confirmed) {
        if (isEditMode && location.state &&  location.state.job_status == 1){
          getAlert("FAILED", "ไม่สามารถลบข้อมูลได้ในขณะนี้");
        setServiceRecords(prev =>
          prev
            .filter(record => record.id !== id)
            .map((record, index) => ({ ...record, itemNo: index + 1 }))
        );
        } else {
          getAlert("FAILED", "ไม่สามารถลบข้อมูลได้ในขณะนี้");
        }
      }
    });
  }, [isEditMode, location.state]);


  const handleSaveRecord = useCallback((record) => {

    if (record.id != null) {
      setServiceRecords(prev =>
        prev.map(r => (r.id === record.id ? record : r))
      );
    } else {
      const newId = Date.now();
      setServiceRecords(prev => [
        ...prev,
        { ...record, id: newId, itemNo: prev.length + 1 },
      ]);
    }
    setEditingRecord(null);
  }, []);

  const validateForm = useCallback(() => {
    const newErrors = {};

    if (!formData.jobNo) newErrors.jobNo = "กรุณากรอก JOB No";
    if (!formData.documentDate) newErrors.documentDate = "กรุณากรอกวันที่";
    if (!formData.customer) newErrors.customer = "กรุณาเลือกลูกค้า";
    if (serviceRecords.length === 0) {
      newErrors.serviceRecords = "กรุณาเพิ่มรายละเอียดบริการ";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData, serviceRecords]);

  const createJobData = useCallback(() => ({
    comp_id: getStorageItem("company"),
    job_no: formData.jobNo,
    job_date: formData.documentDate,
    ar_id: formData.AR_Id,
    Created_By_Name: formData.creator,
    Created_Date: moment().format("YYYY-MM-DD HH:mm:ss"),
    ref_invoice: formData.invoiceRef,
  }), [formData]);

  // Create service records data  
  const createServiceRecordsData = useCallback((jobId) => {
  const userName = getStorageItem("name");
  // time ด้วย
  const currentDate = moment().format("YYYY-MM-DD HH:mm:ss");

  return serviceRecords.map(record => {
    const base = {
      job_time: Number(record.itemNo),
      job_id: Number(jobId),
      srv_date: record.serviceDate ? record.serviceDate : null,
      srv_id: record.serviceType?.value?.id ? Number(record.serviceType.value.id) : null,
      app_date: record.nextAppointment ? record.nextAppointment : null,
      inq_date: record.inquiryDate ? record.inquiryDate : null,
      job_desc: record.desc,
      job_detail: record.detail,
      job_status: record.status?.value ? Number(record.status.value) : null,
      emp_id: record.employee?.id || record.employee?.value?.id || null,
      manners: record.behavior?.value ? Number(record.behavior.value) : null,
      product_correct: record.productOK?.value ? Number(record.productOK.value) : null,
      clean: record.cleanliness?.value ? Number(record.cleanliness.value) : null,
      working_period: record.duration?.value ? Number(record.duration.value) : null,
contact_name: formData.contact || "",
    };

    if (isEditMode) {
      return {
        ...base,
        Update_By_Name: userName,
        Update_Date: currentDate,
      };
    } else {
      return {
        ...base,
        Created_By_Name: userName,
        Created_Date: currentDate,
      };
    }
  });
}, [serviceRecords, formData.customer, formData.contact, isEditMode]);

  // Save service records to database
  const saveServiceRecords = useCallback(async (jobId, serviceRecordsData) => {
    const insertPromises = serviceRecordsData.map(record =>
      insertData(
        "JOB_D",
        Object.keys(record).join(", "),
        Object.values(record)
      )
    );

    await Promise.all(insertPromises);
  }, []);

  // Handle form submission
  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      getAlert("FAILED", "กรุณากรอกข้อมูลให้ครบถ้วน");
      return;
    }

    const jobData = createJobData();

    try {
      let jobId;

      if (isEditMode) {
        jobData.Update_By_Name = getStorageItem("name");
        jobData.Update_Date = moment().format("YYYY-MM-DD HH:mm:ss");
        jobData.job_date = formatThaiDateUiToDate(jobData.job_date);
    
        try {
        await updateData("JOB_H", jobData, { job_no: location.state.job_no });
        jobId = location.state.job_id;
        await deleteData("JOB_D", `WHERE job_id = ${jobId}`);
        }
        catch (error) {
          console.error("Update error:", error);
          getAlert("FAILED", "ไม่สามารถแก้ไขได้");
          return;
        }
      } else {
        // Create new jo
        jobData.job_date = formatThaiDateUiToDate(jobData.job_date);
        jobData.Created_By_Name = getStorageItem("name");
        jobData.Created_Date = moment().format("YYYY-MM-DD HH:mm:ss");
        const result = await insertData(
          "JOB_H",
          Object.keys(jobData).join(", "),
          Object.values(jobData)
        );
        jobId = result?.result?.recordset?.[0]?.insertedId;
      }
      if (!jobId) {
        throw new Error("ไม่สามารถบันทึก JOB_H ได้");
      }

      // Save service records
      const serviceRecordsData = createServiceRecordsData(jobId);
      await saveServiceRecords(jobId, serviceRecordsData);


      // Navigate back and show success message
      navigate("/job-assignment-system");
      setTimeout(() => {
        const message = isEditMode ? "แก้ไขข้อมูลเรียบร้อย" : "บันทึกข้อมูลเรียบร้อย";
        getAlert("OK", message);
      }, 300);
    
    } catch (error) {
      console.error("Submit error:", error);
      getAlert("FAILED", "เกิดข้อผิดพลาดในการบันทึก");
    }
  }, [
    validateForm,
    createJobData,
    createServiceRecordsData,
    saveServiceRecords,
    isEditMode,
    location.state,
    navigate
  ]);


  return (
    <div className="bg-light min-vh-100 mx-3">
      <div className="wrapper">
        <Navbar />
        <main className="main-panel">
          <div className="container-fluid px-3 py-2">
            <Breadcrumbs
              page={`${formData.jobNo}`}
              items={[
                { name: "CRM", url: "/crm" },
                {
                  name: "Job Assignment System",
                  url: "/job-assignment-system",
                },
              ]}
            />
            <div className="card p-2">

              <form onSubmit={handleSubmit} className="card-body">
                {fieldRows.map((row, rowIndex) => (
                  <div className="row mb-2" key={rowIndex}>
                    {row.map((field, colIndex) =>
                      field ? (
                        <div
                          className="col-md-4 d-flex align-items-center "
                          key={colIndex}
                        >
                          <label
                            htmlFor={field.name}
                            className="me-2 mb-0"
                            style={{ whiteSpace: "nowrap", maxWidth: "140px" }}
                          >
                            {field.label}
                            {field.required && (
                              <span className="text-danger ms-1">*</span>
                            )}
                          </label>
                          <div className="flex-grow-1">
                            {field.type === "text" && (
                              <input
                                type="text"
                                id={field.name}
                                name={field.name}
                                className={`form-control ${field.readonly ? "form-readonly" : ""
                                  } ${errors[field.name] ? "is-invalid" : ""}`}
                                value={formData[field.name] || ""}
                                readOnly={!!field.readonly}
                                onChange={handleChange}
                                required={field.required}
                              />
                            )}
                            {field.type === "date" && (
                              <input
                                type="date"
                                id={field.name}
                                name={field.name}
                                className={`form-control ${field.readonly ? "form-readonly" : ""
                                  } ${errors[field.name] ? "is-invalid" : ""}`}
                                value={formData[field.name] || ""}
                                onChange={handleChange}
                                required={field.required}
                              />
                            )}
                            {field.type === "textarea" && (
                              <textarea
                                id={field.name}
                                name={field.name}
                                rows={field.rows || 3}
                                className={`form-control ${field.readonly ? "form-readonly" : ""
                                  } ${errors[field.name] ? "is-invalid" : ""}`}
                                value={formData[field.name] || ""}
                                readOnly={!!field.readonly}
                                onChange={handleChange}
                                required={field.required}
                              />
                            )}
                            {field.type === "select" && (
                              <select
                                id={field.name}
                                name={field.name}
                                className={`form-select ${errors[field.name] ? "is-invalid" : ""
                                  }`}
                                value={formData[field.name] || ""}
                                onChange={handleChange}
                                required={field.required}
                              >
                                <option value="">-- เลือก --</option>
                                {field.options.map((opt) => (
                                  <option key={opt.value} value={opt.value}>
                                    {opt.label}
                                  </option>
                                ))}
                              </select>
                            )}
                            {field.type === "Search" && (
                              <div className="input-group">
                                <input
                                  type="text"
                                  id={field.name}
                                  name={field.name}
                                  className={`form-control ${field.readonly ? "form-readonly" : ""
                                    } ${errors[field.name] ? "is-invalid" : ""}`}
                                  value={formData[field.name] || ""}
                                  readOnly={!!field.readonly}
                                  onChange={handleChange}
                                  required={field.required}
                                />
                                <button
                                  type="button"
                                  className="btn btn-outline-secondary"
                                  onClick={() =>
                                    setActiveModalType(field.table)
                                  }
                                >
                                  <i className="fas fa-search" />
                                </button>
                              </div>
                            )}
                            {errors[field.name] && (
                              <div className="invalid-feedback d-block">
                                {errors[field.name]}
                              </div>
                            )}
                          </div>
                        </div>
                      ) : (
                        <div className="col-md-4" key={colIndex}></div>
                      )
                    )}
                  </div>
                ))}
                {activeModalType && (
                  <ArModal
                    showArModal={!!activeModalType}
                    handleArClose={() => setActiveModalType(null)}
                    type={activeModalType}
                    onRowSelectAr={(ar) => {
                      setFormData((prev) => ({
                        ...prev,
                        AR_Id: ar.AR_Id,
                        customer: ar.AR_Name,
                        address: `${ar.AR_Add1 || ""} ${ar.AR_Add2 || ""} ${ar.AR_Add3 || ""} ${ar.AR_Province || ""} ${ar.AR_zipCode || ""}`,
                        invoiceRef: ar.Doc_No,
                        contact: ar.AR_Contact1 || ar.AR_Contact2 || "",
                        AR_Contact1: ar.AR_Contact1,
                        AR_Contact2: ar.AR_Contact2,
                      }));
                      setActiveModalType(null);
                    }}

                  />
                )}
                <ServiceRecordsTable
                  records={serviceRecords}
                  errors={errors}
                  onEdit={setEditingRecord}
                  onDelete={handleDeleteRecord}
                  onAdd={() => setEditingRecord({})}
                />
                {editingRecord && (
                  <ServiceRecordModal
                    record={editingRecord}
                    onSave={handleSaveRecord}
                    onClose={() => setEditingRecord(null)}
                  />
                )}
                <div className="mt-3 text-end">
                  <button
                    type="button"
                    className="btn btn-black me-2"
                    onClick={() => navigate("/job-assignment-system")}
                  >
                    กลับหน้าค้นหา
                  </button> 
                  <button type="submit" className="btn btn-primary">
                    {location.state ? "แก้ไขข้อมูล" : "บันทึกข้อมูลใหม่"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}
